import React, { useState, useEffect, useCallback } from 'react';
import { fromIso } from '../utils/businessDate';
import { getTenantToday } from '../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';

// ============== Types ==============
type InsuranceSubTab = 'rbc' | 'schedule-d' | 'schedule-ba' | 'sap-recon' | 'alm';

const SUB_TABS = [
  { id: 'rbc' as InsuranceSubTab, label: 'RBC Dashboard', badge: 'Capital' },
  { id: 'schedule-d' as InsuranceSubTab, label: 'Schedule D', badge: 'Bonds' },
  { id: 'schedule-ba' as InsuranceSubTab, label: 'Schedule BA', badge: 'Alts' },
  { id: 'sap-recon' as InsuranceSubTab, label: 'SAP Reconciliation', badge: 'Dual' },
  { id: 'alm' as InsuranceSubTab, label: 'ALM Dashboard', badge: 'Surplus' },
];

interface Portfolio {
  id: number | string;
  portfolioName: string;
}

interface NAICBucket {
  naic: number;
  count: number;
  bookValue: number;
  rbcCharge: number;
}

interface RBCContributor {
  securityName: string;
  naic: number;
  bookValue: number;
  rbcFactor: number;
  chargeAmount: number;
}

interface RBCData {
  rbcRatio: number;
  actionLevel: string;
  naicDistribution: NAICBucket[];
  topContributors: RBCContributor[];
}

interface ScheduleDLineItem {
  cusip: string;
  description: string;
  naicDesignation: number;
  bookValue: number;
  marketValue: number;
  gainLoss: number;
  part: number;
}

interface ScheduleDData {
  id: number;
  status: string;
  filingPeriod: string;
  partSummaries: { part: number; count: number; total: number }[];
  lineItems: ScheduleDLineItem[];
}

interface ScheduleBALineItem {
  fundName: string;
  investmentType: string;
  naicCategory: string;
  costBasis: number;
  bookValue: number;
  fairValue: number;
  commitment: number;
  unfunded: number;
}

interface ScheduleBAData {
  id: number;
  status: string;
  filingPeriod: string;
  totalCostBasis: number;
  totalBookValue: number;
  totalFairValue: number;
  totalIncome: number;
  lineItems: ScheduleBALineItem[];
}

interface SAPReconRow {
  securityName: string;
  cusip: string;
  naic: number;
  gaapValue: number;
  sapValue: number;
  difference: number;
  valuationMethod: string;
  impaired: boolean;
}

interface SAPData {
  totalGAAPValue: number;
  totalSAPValue: number;
  totalDifference: number;
  impairedCount: number;
  rows: SAPReconRow[];
}

interface Liability {
  id: number | string;
  name: string;
}

interface CashFlowBucket {
  period: string;
  assetCF: number;
  liabilityCF: number;
  netCF: number;
  cumulativeSurplus: number;
}

interface KeyRateDuration {
  tenor: string;
  assetKRD: number;
  liabilityKRD: number | null;
  netKRD: number;
}

interface RateShockRow {
  shockBps: number;
  assetImpact: number;
  liabilityImpact: number;
  surplusChange: number;
  newSurplus: number;
  riskLevel: string;
}

interface ALMData {
  totalAssetValue: number;
  totalLiabilityPV: number;
  surplus: number;
  assetDuration: number;
  liabilityDuration: number;
  surplusDuration: number;
  durationGap: number;
  recommendation: string;
  cashFlowBuckets: CashFlowBucket[];
  keyRateDurations: KeyRateDuration[];
  rateShockScenarios: RateShockRow[];
}

// ============== Styled Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  color: #f1f5f9;
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 0.25rem 0;
`;

const PageSubtitle = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
`;

const TabBar = styled.div`
  display: flex;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 6px;
  border-radius: 12px;
  margin-bottom: 2rem;
  overflow-x: auto;
  gap: 4px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.$active ? '#4f46e5' : 'transparent'};
  color: ${props => props.$active ? '#ffffff' : '#64748b'};
  box-shadow: ${props => props.$active ? '0 4px 12px rgba(79, 70, 229, 0.3)' : 'none'};

  &:hover {
    color: ${props => props.$active ? '#ffffff' : '#cbd5e1'};
  }
`;

const TabBadge = styled.span<{ $active: boolean }>`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${props => props.$active ? 'rgba(255,255,255,0.2)' : 'rgba(100,116,139,0.2)'};
  color: ${props => props.$active ? '#e0e7ff' : '#64748b'};
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const Card = styled(GlassCard)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: #f1f5f9;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div<{ $borderColor?: string }>`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid #334155;
  border-left: 4px solid ${props => props.$borderColor || '#3b82f6'};
  border-radius: 10px;
  padding: 16px;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #f1f5f9;
  font-family: 'JetBrains Mono', monospace;
`;

const StatSub = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  margin-top: 2px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.5);
`;

const ThRight = styled(Th)`
  text-align: right;
`;

const Td = styled.td`
  padding: 12px;
  color: #e2e8f0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  font-size: 0.875rem;
`;

const TdMono = styled(Td)`
  font-family: 'JetBrains Mono', monospace;
  text-align: right;
`;

const TrHighlight = styled.tr<{ $variant?: 'amber' | 'red' | 'none' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'amber': return 'rgba(234, 179, 8, 0.08)';
      case 'red': return 'rgba(239, 68, 68, 0.08)';
      default: return 'transparent';
    }
  }};
  &:hover {
    background: rgba(148, 163, 184, 0.05);
  }
`;

const Badge = styled.span<{ $type: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  ${props => {
    switch (props.$type) {
      case 'green':
        return 'background: rgba(16, 185, 129, 0.2); color: #34d399;';
      case 'amber':
        return 'background: rgba(234, 179, 8, 0.2); color: #facc15;';
      case 'red':
        return 'background: rgba(239, 68, 68, 0.2); color: #f87171;';
      case 'DRAFT':
        return 'background: rgba(234, 179, 8, 0.2); color: #facc15;';
      case 'REVIEWED':
        return 'background: rgba(59, 130, 246, 0.2); color: #60a5fa;';
      case 'SUBMITTED':
        return 'background: rgba(16, 185, 129, 0.2); color: #34d399;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        `;
      case 'secondary':
        return `
          background: rgba(148, 163, 184, 0.2);
          color: #e2e8f0;
          border: 1px solid rgba(148, 163, 184, 0.3);
        `;
      default:
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
        `;
    }
  }}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Select = styled.select`
  padding: 10px 14px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Input = styled.input`
  padding: 10px 14px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const GaugeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const GaugeValue = styled.div<{ $color: string }>`
  font-size: 4rem;
  font-weight: 900;
  color: ${props => props.$color};
  font-family: 'JetBrains Mono', monospace;
  line-height: 1;
`;

const GaugeLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
  margin-top: 8px;
  font-weight: 600;
`;

const GaugeActionLevel = styled.div<{ $color: string }>`
  margin-top: 12px;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  background: ${props => props.$color}22;
  color: ${props => props.$color};
  border: 1px solid ${props => props.$color}44;
`;

const NAICGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const NAICCard = styled.div<{ $naic: number }>`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 16px;
  border-top: 3px solid ${props => {
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#f97316', '#ef4444', '#dc2626'];
    return colors[props.$naic - 1] || '#64748b';
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #64748b;
  font-size: 0.95rem;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1rem;
`;

const PartCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SurplusCard = styled(Card)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.5rem;
  align-items: start;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 12px;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 0.85rem;
  font-family: 'JetBrains Mono', monospace;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #94a3b8;
  font-size: 0.875rem;
  gap: 8px;

  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid rgba(148, 163, 184, 0.3);
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorBanner = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  color: #f87171;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

// ============== Helpers ==============
const fmt = (n: number | null | undefined, decimals = 0): string => {
  if (n == null || isNaN(n)) return '--';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtCurrency = (n: number | null | undefined): string => {
  if (n == null || isNaN(n)) return '--';
  if (Math.abs(n) >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtPct = (n: number | null | undefined, decimals = 2): string => {
  if (n == null || isNaN(n)) return '--';
  return `${n.toFixed(decimals)}%`;
};

const rbcColor = (ratio: number): string => {
  if (ratio >= 3) return '#10b981';
  if (ratio >= 2) return '#f59e0b';
  return '#ef4444';
};

const rbcActionLevel = (ratio: number): string => {
  if (ratio >= 3) return 'NONE';
  if (ratio >= 2.5) return 'COMPANY ACTION';
  if (ratio >= 2) return 'REGULATORY ACTION';
  if (ratio >= 1.5) return 'AUTHORIZED CONTROL';
  return 'MANDATORY CONTROL';
};

const riskLevelColor = (level: string): string => {
  const l = level?.toLowerCase() || '';
  if (l === 'low' || l === 'green') return 'green';
  if (l === 'medium' || l === 'amber' || l === 'moderate') return 'amber';
  return 'red';
};

// ============== Component ==============
const InsuranceReportingScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InsuranceSubTab>('rbc');
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // RBC state
  const [rbcData, setRbcData] = useState<RBCData | null>(null);

  // Schedule D state
  const [scheduleDData, setScheduleDData] = useState<ScheduleDData | null>(null);
  const [scheduleDYear, setScheduleDYear] = useState<string>(String(fromIso(getTenantToday()).getFullYear()));
  const [scheduleDPartFilter, setScheduleDPartFilter] = useState<number>(0);

  // Schedule BA state
  const [scheduleBAData, setScheduleBAData] = useState<ScheduleBAData | null>(null);
  const [scheduleBAYear, setScheduleBAYear] = useState<string>(String(fromIso(getTenantToday()).getFullYear()));

  // SAP state
  const [sapData, setSapData] = useState<SAPData | null>(null);

  // ALM state
  const [almData, setAlmData] = useState<ALMData | null>(null);
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [selectedLiabilityId, setSelectedLiabilityId] = useState<string>('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCsvText, setImportCsvText] = useState('');

  // ============== Data Fetching ==============
  const fetchPortfolios = useCallback(async () => {
    try {
      const response = await axios.get('/api/dimensions/portfolios');
      const data = response.data?.content || response.data || [];
      const list = Array.isArray(data) ? data : [];
      setPortfolios(list);
      if (list.length > 0 && !selectedPortfolioId) {
        setSelectedPortfolioId(String(list[0].id));
      }
    } catch (err: any) {
      console.error('Failed to load portfolios', err);
    }
  }, [selectedPortfolioId]);

  const fetchLiabilities = useCallback(async () => {
    try {
      const response = await axios.get('/api/insurance/liabilities');
      const data = response.data || [];
      setLiabilities(Array.isArray(data) ? data : []);
    } catch {
      // Liabilities may not exist yet
    }
  }, []);

  useEffect(() => {
    fetchPortfolios();
    fetchLiabilities();
  }, [fetchPortfolios, fetchLiabilities]);

  // ============== RBC ==============
  const calculateRBC = useCallback(async () => {
    if (!selectedPortfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`/api/insurance/rbc/calculate/${selectedPortfolioId}`);
      setRbcData(response.data);
    } catch (err: any) {
      setError('Failed to calculate RBC: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolioId]);

  // ============== Schedule D ==============
  const generateScheduleD = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/api/insurance/schedule-d/generate', {
        tenantId: 1,
        filingPeriod: scheduleDYear,
        periodEndDate: `${scheduleDYear}-12-31`,
      });
      const id = response.data?.id;
      if (id) {
        const detail = await axios.get(`/api/insurance/schedule-d/${id}`);
        setScheduleDData(detail.data);
      } else {
        setScheduleDData(response.data);
      }
    } catch (err: any) {
      setError('Failed to generate Schedule D: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [scheduleDYear]);

  // ============== Schedule BA ==============
  const generateScheduleBA = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/api/insurance/schedule-ba/generate', {
        tenantId: 1,
        filingPeriod: scheduleBAYear,
        periodEndDate: `${scheduleBAYear}-12-31`,
      });
      setScheduleBAData(response.data);
    } catch (err: any) {
      setError('Failed to generate Schedule BA: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [scheduleBAYear]);

  // ============== SAP Reconciliation ==============
  const runStatutoryValuation = useCallback(async () => {
    if (!selectedPortfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`/api/insurance/statutory/valuate/${selectedPortfolioId}`);
      setSapData(response.data);
    } catch (err: any) {
      setError('Failed to run statutory valuation: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolioId]);

  // ============== ALM ==============
  const runALMAnalysis = useCallback(async () => {
    if (!selectedPortfolioId || !selectedLiabilityId) return;
    setLoading(true);
    setError(null);
    try {
      const [surplusRes, cfRes, krdRes, shockRes] = await Promise.all([
        axios.get(`/api/insurance/alm/surplus/${selectedPortfolioId}/${selectedLiabilityId}`),
        axios.get(`/api/insurance/alm/cashflow-match/${selectedPortfolioId}/${selectedLiabilityId}`),
        axios.get(`/api/insurance/alm/key-rate-durations/${selectedPortfolioId}`),
        axios.post('/api/insurance/alm/rate-shock', {
          portfolioId: selectedPortfolioId,
          liabilityId: selectedLiabilityId,
          shocksBps: [-200, -100, -50, 50, 100, 200],
        }),
      ]);

      setAlmData({
        totalAssetValue: surplusRes.data.totalAssetValue,
        totalLiabilityPV: surplusRes.data.totalLiabilityPV,
        surplus: surplusRes.data.surplus,
        assetDuration: surplusRes.data.assetDuration,
        liabilityDuration: surplusRes.data.liabilityDuration,
        surplusDuration: surplusRes.data.surplusDuration,
        durationGap: surplusRes.data.durationGap,
        recommendation: surplusRes.data.recommendation,
        cashFlowBuckets: cfRes.data || [],
        keyRateDurations: krdRes.data || [],
        rateShockScenarios: shockRes.data || [],
      });
    } catch (err: any) {
      setError('Failed to run ALM analysis: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolioId, selectedLiabilityId]);

  const importLiabilities = useCallback(async () => {
    if (!importCsvText.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post('/api/insurance/liabilities/import', { csvData: importCsvText });
      setShowImportModal(false);
      setImportCsvText('');
      await fetchLiabilities();
    } catch (err: any) {
      setError('Failed to import liabilities: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [importCsvText, fetchLiabilities]);

  // ============== Renderers ==============

  const renderRBCTab = () => (
    <>
      <ControlsRow>
        <Select value={selectedPortfolioId} onChange={e => setSelectedPortfolioId(e.target.value)}>
          <option value="">Select Portfolio</option>
          {portfolios.map(p => (
            <option key={p.id} value={String(p.id)}>{p.portfolioName}</option>
          ))}
        </Select>
        <Button onClick={calculateRBC} disabled={!selectedPortfolioId || loading}>
          {loading ? 'Calculating...' : 'Calculate RBC'}
        </Button>
      </ControlsRow>

      {!rbcData && !loading && (
        <EmptyState>No data -- click Calculate RBC to run analysis</EmptyState>
      )}

      {rbcData && (
        <>
          <GridRow>
            <Card>
              <CardTitle>RBC Ratio</CardTitle>
              <GaugeContainer>
                <GaugeValue $color={rbcColor(rbcData.rbcRatio)}>
                  {rbcData.rbcRatio.toFixed(2)}x
                </GaugeValue>
                <GaugeLabel>Risk-Based Capital Ratio</GaugeLabel>
                <GaugeActionLevel $color={rbcColor(rbcData.rbcRatio)}>
                  Action Level: {rbcData.actionLevel || rbcActionLevel(rbcData.rbcRatio)}
                </GaugeActionLevel>
              </GaugeContainer>
            </Card>
            <Card>
              <CardTitle>NAIC Distribution</CardTitle>
              <NAICGrid>
                {(rbcData.naicDistribution || []).map(bucket => (
                  <NAICCard key={bucket.naic} $naic={bucket.naic}>
                    <StatLabel>NAIC {bucket.naic}</StatLabel>
                    <StatNumber style={{ fontSize: '1.25rem' }}>{fmt(bucket.count)}</StatNumber>
                    <StatSub>Book: {fmtCurrency(bucket.bookValue)}</StatSub>
                    <StatSub>Charge: {fmtCurrency(bucket.rbcCharge)}</StatSub>
                  </NAICCard>
                ))}
              </NAICGrid>
            </Card>
          </GridRow>

          <Card>
            <CardTitle>Top RBC Contributors</CardTitle>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <Th>Security Name</Th>
                    <Th>NAIC</Th>
                    <ThRight>Book Value</ThRight>
                    <ThRight>RBC Factor</ThRight>
                    <ThRight>Charge Amount</ThRight>
                  </tr>
                </thead>
                <tbody>
                  {(rbcData.topContributors || []).slice(0, 10).map((row, i) => (
                    <tr key={i}>
                      <Td>{row.securityName}</Td>
                      <Td><Badge $type={row.naic <= 2 ? 'green' : row.naic <= 4 ? 'amber' : 'red'}>{row.naic}</Badge></Td>
                      <TdMono>{fmtCurrency(row.bookValue)}</TdMono>
                      <TdMono>{fmtPct(row.rbcFactor * 100, 3)}</TdMono>
                      <TdMono>{fmtCurrency(row.chargeAmount)}</TdMono>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {(!rbcData.topContributors || rbcData.topContributors.length === 0) && (
                <EmptyState>No contributor data available</EmptyState>
              )}
            </div>
          </Card>
        </>
      )}
    </>
  );

  const renderScheduleDTab = () => {
    const filteredItems = scheduleDData?.lineItems?.filter(
      item => scheduleDPartFilter === 0 || item.part === scheduleDPartFilter
    ) || [];

    return (
      <>
        <ControlsRow>
          <Input
            type="number"
            value={scheduleDYear}
            onChange={e => setScheduleDYear(e.target.value)}
            style={{ width: 120 }}
            placeholder="Year"
            min="2000"
            max="2099"
          />
          <Button onClick={generateScheduleD} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Schedule D'}
          </Button>
          {scheduleDData && (
            <>
              <Button $variant="secondary" onClick={() => window.alert('Excel export triggered')}>
                Export Excel
              </Button>
              <Button $variant="secondary" onClick={() => window.alert('XML export triggered')}>
                Export XML
              </Button>
            </>
          )}
        </ControlsRow>

        {!scheduleDData && !loading && (
          <EmptyState>No data -- click Generate Schedule D to create a filing</EmptyState>
        )}

        {scheduleDData && (
          <>
            <ControlsRow>
              <Badge $type={scheduleDData.status || 'DRAFT'}>
                {scheduleDData.status || 'DRAFT'}
              </Badge>
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                Filing Period: {scheduleDData.filingPeriod}
              </span>
            </ControlsRow>

            <PartCards>
              {(scheduleDData.partSummaries || []).map(ps => (
                <StatCard key={ps.part} $borderColor="#3b82f6">
                  <StatLabel>Part {ps.part}</StatLabel>
                  <StatNumber style={{ fontSize: '1.25rem' }}>{fmt(ps.count)}</StatNumber>
                  <StatSub>Total: {fmtCurrency(ps.total)}</StatSub>
                </StatCard>
              ))}
            </PartCards>

            <Card>
              <CardTitle>Line Items</CardTitle>
              <FilterRow>
                <Select
                  value={scheduleDPartFilter}
                  onChange={e => setScheduleDPartFilter(Number(e.target.value))}
                  style={{ width: 160 }}
                >
                  <option value={0}>All Parts</option>
                  <option value={1}>Part 1</option>
                  <option value={2}>Part 2</option>
                  <option value={3}>Part 3</option>
                  <option value={4}>Part 4</option>
                  <option value={5}>Part 5</option>
                </Select>
                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  {filteredItems.length} items
                </span>
              </FilterRow>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <Th>CUSIP</Th>
                      <Th>Description</Th>
                      <Th>NAIC</Th>
                      <Th>Part</Th>
                      <ThRight>Book Value</ThRight>
                      <ThRight>Market Value</ThRight>
                      <ThRight>Gain/Loss</ThRight>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.slice(0, 100).map((item, i) => (
                      <tr key={i}>
                        <Td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{item.cusip}</Td>
                        <Td>{item.description}</Td>
                        <Td>{item.naicDesignation}</Td>
                        <Td>{item.part}</Td>
                        <TdMono>{fmtCurrency(item.bookValue)}</TdMono>
                        <TdMono>{fmtCurrency(item.marketValue)}</TdMono>
                        <TdMono style={{ color: item.gainLoss >= 0 ? '#34d399' : '#f87171' }}>
                          {fmtCurrency(item.gainLoss)}
                        </TdMono>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {filteredItems.length === 0 && (
                  <EmptyState>No line items found</EmptyState>
                )}
              </div>
            </Card>
          </>
        )}
      </>
    );
  };

  const renderScheduleBATab = () => (
    <>
      <ControlsRow>
        <Input
          type="number"
          value={scheduleBAYear}
          onChange={e => setScheduleBAYear(e.target.value)}
          style={{ width: 120 }}
          placeholder="Year"
          min="2000"
          max="2099"
        />
        <Button onClick={generateScheduleBA} disabled={loading}>
          {loading ? 'Generating...' : 'Generate Schedule BA'}
        </Button>
        {scheduleBAData && (
          <Button $variant="secondary" onClick={() => window.alert('Export triggered')}>
            Export Excel
          </Button>
        )}
      </ControlsRow>

      {!scheduleBAData && !loading && (
        <EmptyState>No data -- click Generate Schedule BA to create a filing</EmptyState>
      )}

      {scheduleBAData && (
        <>
          <ControlsRow>
            <Badge $type={scheduleBAData.status || 'DRAFT'}>
              {scheduleBAData.status || 'DRAFT'}
            </Badge>
            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
              Filing Period: {scheduleBAData.filingPeriod}
            </span>
          </ControlsRow>

          <StatsGrid>
            <StatCard $borderColor="#3b82f6">
              <StatLabel>Total Cost Basis</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem' }}>{fmtCurrency(scheduleBAData.totalCostBasis)}</StatNumber>
            </StatCard>
            <StatCard $borderColor="#8b5cf6">
              <StatLabel>Total Book Value</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem' }}>{fmtCurrency(scheduleBAData.totalBookValue)}</StatNumber>
            </StatCard>
            <StatCard $borderColor="#10b981">
              <StatLabel>Total Fair Value</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem' }}>{fmtCurrency(scheduleBAData.totalFairValue)}</StatNumber>
            </StatCard>
            <StatCard $borderColor="#f59e0b">
              <StatLabel>Total Income</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem' }}>{fmtCurrency(scheduleBAData.totalIncome)}</StatNumber>
            </StatCard>
          </StatsGrid>

          <Card>
            <CardTitle>Alternative Asset Line Items</CardTitle>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <Th>Fund Name</Th>
                    <Th>Investment Type</Th>
                    <Th>NAIC Category</Th>
                    <ThRight>Cost Basis</ThRight>
                    <ThRight>Book Value</ThRight>
                    <ThRight>Fair Value</ThRight>
                    <ThRight>Commitment</ThRight>
                    <ThRight>Unfunded</ThRight>
                  </tr>
                </thead>
                <tbody>
                  {(scheduleBAData.lineItems || []).map((item, i) => (
                    <tr key={i}>
                      <Td>{item.fundName}</Td>
                      <Td>{item.investmentType}</Td>
                      <Td>{item.naicCategory}</Td>
                      <TdMono>{fmtCurrency(item.costBasis)}</TdMono>
                      <TdMono>{fmtCurrency(item.bookValue)}</TdMono>
                      <TdMono>{fmtCurrency(item.fairValue)}</TdMono>
                      <TdMono>{fmtCurrency(item.commitment)}</TdMono>
                      <TdMono>{fmtCurrency(item.unfunded)}</TdMono>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {(!scheduleBAData.lineItems || scheduleBAData.lineItems.length === 0) && (
                <EmptyState>No line items found</EmptyState>
              )}
            </div>
          </Card>
        </>
      )}
    </>
  );

  const renderSAPReconTab = () => (
    <>
      <ControlsRow>
        <Select value={selectedPortfolioId} onChange={e => setSelectedPortfolioId(e.target.value)}>
          <option value="">Select Portfolio</option>
          {portfolios.map(p => (
            <option key={p.id} value={String(p.id)}>{p.portfolioName}</option>
          ))}
        </Select>
        <Button onClick={runStatutoryValuation} disabled={!selectedPortfolioId || loading}>
          {loading ? 'Running...' : 'Run Statutory Valuation'}
        </Button>
      </ControlsRow>

      {!sapData && !loading && (
        <EmptyState>No data -- click Run Statutory Valuation to compare GAAP vs SAP</EmptyState>
      )}

      {sapData && (
        <>
          <StatsGrid>
            <StatCard $borderColor="#3b82f6">
              <StatLabel>Total GAAP Value</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem' }}>{fmtCurrency(sapData.totalGAAPValue)}</StatNumber>
            </StatCard>
            <StatCard $borderColor="#8b5cf6">
              <StatLabel>Total SAP Value</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem' }}>{fmtCurrency(sapData.totalSAPValue)}</StatNumber>
            </StatCard>
            <StatCard $borderColor={sapData.totalDifference === 0 ? '#10b981' : '#f59e0b'}>
              <StatLabel>Difference</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem', color: sapData.totalDifference === 0 ? '#34d399' : '#facc15' }}>
                {fmtCurrency(sapData.totalDifference)}
              </StatNumber>
            </StatCard>
            <StatCard $borderColor={sapData.impairedCount === 0 ? '#10b981' : '#ef4444'}>
              <StatLabel>Impaired Count</StatLabel>
              <StatNumber style={{ fontSize: '1.25rem', color: sapData.impairedCount === 0 ? '#34d399' : '#f87171' }}>
                {fmt(sapData.impairedCount)}
              </StatNumber>
            </StatCard>
          </StatsGrid>

          <Card>
            <CardTitle>Reconciliation Details</CardTitle>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <Th>Security Name</Th>
                    <Th>CUSIP</Th>
                    <Th>NAIC</Th>
                    <ThRight>GAAP Value</ThRight>
                    <ThRight>SAP Value</ThRight>
                    <ThRight>Difference</ThRight>
                    <Th>Valuation Method</Th>
                    <Th>Impaired</Th>
                  </tr>
                </thead>
                <tbody>
                  {(sapData.rows || []).map((row, i) => {
                    const variant = row.impaired ? 'red' : (row.difference !== 0 ? 'amber' : 'none');
                    return (
                      <TrHighlight key={i} $variant={variant}>
                        <Td>{row.securityName}</Td>
                        <Td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.cusip}</Td>
                        <Td>{row.naic}</Td>
                        <TdMono>{fmtCurrency(row.gaapValue)}</TdMono>
                        <TdMono>{fmtCurrency(row.sapValue)}</TdMono>
                        <TdMono style={{ color: row.difference === 0 ? '#94a3b8' : '#facc15' }}>
                          {fmtCurrency(row.difference)}
                        </TdMono>
                        <Td>{row.valuationMethod}</Td>
                        <Td>
                          <Badge $type={row.impaired ? 'red' : 'green'}>
                            {row.impaired ? 'YES' : 'NO'}
                          </Badge>
                        </Td>
                      </TrHighlight>
                    );
                  })}
                </tbody>
              </Table>
              {(!sapData.rows || sapData.rows.length === 0) && (
                <EmptyState>No reconciliation data available</EmptyState>
              )}
            </div>
          </Card>
        </>
      )}
    </>
  );

  const renderALMTab = () => (
    <>
      <ControlsRow>
        <Select value={selectedPortfolioId} onChange={e => setSelectedPortfolioId(e.target.value)}>
          <option value="">Select Portfolio</option>
          {portfolios.map(p => (
            <option key={p.id} value={String(p.id)}>{p.portfolioName}</option>
          ))}
        </Select>
        <Select value={selectedLiabilityId} onChange={e => setSelectedLiabilityId(e.target.value)}>
          <option value="">Select Liability</option>
          {liabilities.map(l => (
            <option key={l.id} value={String(l.id)}>{l.name}</option>
          ))}
        </Select>
        <Button onClick={runALMAnalysis} disabled={!selectedPortfolioId || !selectedLiabilityId || loading}>
          {loading ? 'Analyzing...' : 'Run ALM Analysis'}
        </Button>
        <Button $variant="secondary" onClick={() => setShowImportModal(true)}>
          Import Liabilities
        </Button>
      </ControlsRow>

      {!almData && !loading && (
        <EmptyState>No data -- select a portfolio and liability, then click Run ALM Analysis</EmptyState>
      )}

      {almData && (
        <>
          {/* Surplus Monitor */}
          <Card>
            <CardTitle>Surplus Monitor</CardTitle>
            <SurplusCard as="div" style={{ padding: 0, background: 'transparent', border: 'none', marginBottom: 0 }}>
              <div>
                <StatLabel>Total Asset Value</StatLabel>
                <StatNumber>{fmtCurrency(almData.totalAssetValue)}</StatNumber>
              </div>
              <div>
                <StatLabel>Total Liability PV</StatLabel>
                <StatNumber>{fmtCurrency(almData.totalLiabilityPV)}</StatNumber>
              </div>
              <div>
                <StatLabel>Surplus</StatLabel>
                <StatNumber style={{ color: almData.surplus >= 0 ? '#34d399' : '#f87171' }}>
                  {fmtCurrency(almData.surplus)}
                </StatNumber>
              </div>
              <div>
                <StatLabel>Asset Duration</StatLabel>
                <StatNumber>{fmt(almData.assetDuration, 2)}y</StatNumber>
              </div>
              <div>
                <StatLabel>Liability Duration</StatLabel>
                <StatNumber>{fmt(almData.liabilityDuration, 2)}y</StatNumber>
              </div>
              <div>
                <StatLabel>Duration Gap</StatLabel>
                <StatNumber style={{ color: Math.abs(almData.durationGap) <= 0.5 ? '#34d399' : Math.abs(almData.durationGap) <= 1.5 ? '#facc15' : '#f87171' }}>
                  {fmt(almData.durationGap, 2)}y
                </StatNumber>
              </div>
            </SurplusCard>
            {almData.recommendation && (
              <div style={{ marginTop: '1rem', padding: '12px 16px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: 10, color: '#94a3b8', fontSize: '0.85rem' }}>
                <span style={{ color: '#60a5fa', fontWeight: 700 }}>Recommendation: </span>
                {almData.recommendation}
              </div>
            )}
          </Card>

          <GridRow>
            {/* Cash Flow Matching */}
            <Card>
              <CardTitle>Cash Flow Matching (Quarterly)</CardTitle>
              <div style={{ overflowX: 'auto', maxHeight: 500 }}>
                <Table>
                  <thead>
                    <tr>
                      <Th>Period</Th>
                      <ThRight>Asset CF</ThRight>
                      <ThRight>Liability CF</ThRight>
                      <ThRight>Net CF</ThRight>
                      <ThRight>Cumul. Surplus</ThRight>
                    </tr>
                  </thead>
                  <tbody>
                    {(almData.cashFlowBuckets || []).slice(0, 20).map((bucket, i) => (
                      <TrHighlight key={i} $variant={bucket.netCF < 0 ? 'red' : 'none'}>
                        <Td>{bucket.period}</Td>
                        <TdMono>{fmtCurrency(bucket.assetCF)}</TdMono>
                        <TdMono>{fmtCurrency(bucket.liabilityCF)}</TdMono>
                        <TdMono style={{ color: bucket.netCF >= 0 ? '#34d399' : '#f87171' }}>
                          {fmtCurrency(bucket.netCF)}
                        </TdMono>
                        <TdMono style={{ color: bucket.cumulativeSurplus >= 0 ? '#34d399' : '#f87171' }}>
                          {fmtCurrency(bucket.cumulativeSurplus)}
                        </TdMono>
                      </TrHighlight>
                    ))}
                  </tbody>
                </Table>
                {(!almData.cashFlowBuckets || almData.cashFlowBuckets.length === 0) && (
                  <EmptyState>No cash flow data available</EmptyState>
                )}
              </div>
            </Card>

            {/* Key Rate Durations */}
            <Card>
              <CardTitle>Key Rate Durations</CardTitle>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <Th>Tenor</Th>
                      <ThRight>Asset KRD</ThRight>
                      <ThRight>Liability KRD</ThRight>
                      <ThRight>Net KRD</ThRight>
                    </tr>
                  </thead>
                  <tbody>
                    {(almData.keyRateDurations || []).map((krd, i) => {
                      const mismatch = Math.abs(krd.netKRD) > 0.5;
                      return (
                        <TrHighlight key={i} $variant={mismatch ? 'amber' : 'none'}>
                          <Td style={{ fontWeight: 600 }}>{krd.tenor}</Td>
                          <TdMono>{fmt(krd.assetKRD, 3)}</TdMono>
                          <TdMono>{krd.liabilityKRD != null ? fmt(krd.liabilityKRD, 3) : '--'}</TdMono>
                          <TdMono style={{ color: mismatch ? '#facc15' : '#94a3b8' }}>
                            {fmt(krd.netKRD, 3)}
                          </TdMono>
                        </TrHighlight>
                      );
                    })}
                  </tbody>
                </Table>
                {(!almData.keyRateDurations || almData.keyRateDurations.length === 0) && (
                  <EmptyState>No key rate duration data available</EmptyState>
                )}
              </div>
            </Card>
          </GridRow>

          {/* Rate Shock Scenarios */}
          <Card>
            <CardTitle>Rate Shock Scenarios</CardTitle>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <ThRight>Shock (bps)</ThRight>
                    <ThRight>Asset Impact</ThRight>
                    <ThRight>Liability Impact</ThRight>
                    <ThRight>Surplus Change</ThRight>
                    <ThRight>New Surplus</ThRight>
                    <Th>Risk Level</Th>
                  </tr>
                </thead>
                <tbody>
                  {(almData.rateShockScenarios || []).map((row, i) => (
                    <tr key={i}>
                      <TdMono style={{ fontWeight: 700, color: row.shockBps < 0 ? '#f87171' : row.shockBps > 0 ? '#34d399' : '#94a3b8' }}>
                        {row.shockBps > 0 ? '+' : ''}{row.shockBps}
                      </TdMono>
                      <TdMono>{fmtCurrency(row.assetImpact)}</TdMono>
                      <TdMono>{fmtCurrency(row.liabilityImpact)}</TdMono>
                      <TdMono style={{ color: row.surplusChange >= 0 ? '#34d399' : '#f87171' }}>
                        {fmtCurrency(row.surplusChange)}
                      </TdMono>
                      <TdMono style={{ color: row.newSurplus >= 0 ? '#34d399' : '#f87171' }}>
                        {fmtCurrency(row.newSurplus)}
                      </TdMono>
                      <Td>
                        <Badge $type={riskLevelColor(row.riskLevel)}>
                          {row.riskLevel}
                        </Badge>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {(!almData.rateShockScenarios || almData.rateShockScenarios.length === 0) && (
                <EmptyState>No rate shock data available</EmptyState>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Import Liabilities Modal */}
      {showImportModal && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CardTitle>Import Liabilities</CardTitle>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Paste CSV data with columns: name, present_value, duration, cash_flows (quarterly, semicolon-separated)
            </p>
            <Textarea
              value={importCsvText}
              onChange={e => setImportCsvText(e.target.value)}
              placeholder="name,present_value,duration,cash_flows&#10;Policy Block A,250000000,8.5,5000000;5000000;5100000..."
            />
            <div style={{ display: 'flex', gap: 12, marginTop: '1rem', justifyContent: 'flex-end' }}>
              <Button $variant="secondary" onClick={() => setShowImportModal(false)}>Cancel</Button>
              <Button onClick={importLiabilities} disabled={!importCsvText.trim() || loading}>
                {loading ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );

  // ============== Main Render ==============
  return (
    <Container>
      <PageHeader>
        <PageTitle>Insurance Reporting</PageTitle>
        <PageSubtitle>
          Statutory reporting, RBC analysis, SAP reconciliation, and asset-liability management
        </PageSubtitle>
      </PageHeader>

      <TabBar>
        {SUB_TABS.map(tab => (
          <TabButton
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <TabBadge $active={activeTab === tab.id}>{tab.badge}</TabBadge>
          </TabButton>
        ))}
      </TabBar>

      {error && <ErrorBanner>{error}</ErrorBanner>}
      {loading && <LoadingSpinner>Processing...</LoadingSpinner>}

      {activeTab === 'rbc' && renderRBCTab()}
      {activeTab === 'schedule-d' && renderScheduleDTab()}
      {activeTab === 'schedule-ba' && renderScheduleBATab()}
      {activeTab === 'sap-recon' && renderSAPReconTab()}
      {activeTab === 'alm' && renderALMTab()}
    </Container>
  );
};

export default InsuranceReportingScreen;
